import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, WarningCircle, X } from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';

export interface Toast {
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
}

interface ToastProps {
    toast: Toast;
    onClose: (id: string) => void;
    isDarkPage?: boolean;
}

export const ToastNotification: React.FC<ToastProps> = ({ toast, onClose, isDarkPage = false }) => {
    const [isVisible, setIsVisible] = useState(false);

    // Longer display for errors so users can read the message
    const duration = toast.type === 'error' ? 8000 : toast.type === 'warning' ? 6000 : 4000;

    useEffect(() => {
        // Trigger enter animation
        requestAnimationFrame(() => setIsVisible(true));

        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(() => onClose(toast.id), 300);
        }, duration);

        return () => clearTimeout(timer);
    }, [toast.id, onClose, duration]);

    const getAccentColor = () => {
        switch (toast.type) {
            case 'success':
                return isDarkPage ? 'border-l-green-400' : 'border-l-green-500 dark:border-l-green-400';
            case 'error':
                return isDarkPage ? 'border-l-brand-orange' : 'border-l-red-500 dark:border-l-red-400';
            case 'warning':
                return isDarkPage ? 'border-l-amber-400' : 'border-l-amber-500 dark:border-l-amber-400';
            case 'info':
            default:
                return isDarkPage ? 'border-l-brand-blue' : 'border-l-blue-500 dark:border-l-blue-400';
        }
    };

    const getIcon = () => {
        const iconClass = isDarkPage ? 'text-white/80' : '';
        switch (toast.type) {
            case 'success':
                return <CheckCircle size={18} className={iconClass || 'text-green-500 dark:text-green-400'} />;
            case 'error':
                return <XCircle size={18} className={isDarkPage ? 'text-brand-orange' : 'text-red-500 dark:text-red-400'} />;
            case 'warning':
                return <WarningCircle size={18} className={iconClass || 'text-amber-500 dark:text-amber-400'} />;
            case 'info':
            default:
                return <WarningCircle size={18} className={isDarkPage ? 'text-brand-blue' : 'text-blue-500 dark:text-blue-400'} />;
        }
    };

    // Dark editorial style for public pages (landing, auth, register, forgot-password)
    if (isDarkPage) {
        return (
            <div
                className={`
                    bg-black/95 backdrop-blur-sm border border-white/10 border-l-2 ${getAccentColor()}
                    px-5 py-4 flex items-center gap-4 min-w-[320px] max-w-md
                    transition-all duration-300 ease-out
                    ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
                `}
            >
                {getIcon()}
                <span className="flex-1 font-mono text-xs uppercase tracking-wider text-white/90">
                    {toast.message}
                </span>
                <button
                    onClick={() => {
                        setIsVisible(false);
                        setTimeout(() => onClose(toast.id), 300);
                    }}
                    className="text-white/40 hover:text-white transition-colors"
                >
                    <X size={16} />
                </button>
            </div>
        );
    }

    // Theme-aware style for app pages
    return (
        <div
            className={`
                bg-card border border-border border-l-4 ${getAccentColor()}
                rounded-lg shadow-lg p-4 flex items-center gap-3 min-w-[300px] max-w-md
                transition-all duration-300 ease-out
                ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
            `}
        >
            {getIcon()}
            <span className="flex-1 text-foreground text-sm font-medium">
                {toast.message}
            </span>
            <button
                onClick={() => {
                    setIsVisible(false);
                    setTimeout(() => onClose(toast.id), 300);
                }}
                className="text-muted-foreground hover:text-foreground transition-colors"
            >
                <X size={18} />
            </button>
        </div>
    );
};

interface ToastContainerProps {
    toasts: Toast[];
    onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => {
    const location = useLocation();

    // Check if we're on a dark-themed public page
    const isDarkPage = ['/', '/login', '/register', '/forgot-password'].includes(location.pathname);

    return (
        <div className="fixed top-6 right-6 z-[300] flex flex-col gap-3">
            {toasts.map(toast => (
                <ToastNotification
                    key={toast.id}
                    toast={toast}
                    onClose={onClose}
                    isDarkPage={isDarkPage}
                />
            ))}
        </div>
    );
};
